import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Controller,
  FieldError,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { coursesCreateInterface } from "../../api/types/types";
import {
  useGetCourseById,
  useUpdateCourse,
} from "../../api/useProducts/useCourses";
import { CustomButton, InputField, Sidebar } from "../../components";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import FileUploadIcon from "../../components/FileUploadIcon";
import Loader from "../../components/Loader";
import useSingleFileUpload from "../../hook/useSingleFileUpload";

const EditCourses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate, isPending: coursePending } = useUpdateCourse(Number(id));

  const { CourseData, CourseLoading, CourseRefetch } = useGetCourseById(
    Number(id)
  );

  const { uploadImage, isUploading } = useSingleFileUpload();
  const [activeTab, setActiveTab] = useState(false);
  const query = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<coursesCreateInterface>();
  const [uploadedData, setUploadedData] = useState<File>();

  const onSubmit: SubmitHandler<coursesCreateInterface> = async (data) => {
    let imageUrl;
    if (uploadedData) {
      imageUrl = await uploadImage(uploadedData);
    }
    mutate(
      {
        ...(data.title && { title: data.title }),
        ...(uploadedData && { image: imageUrl }),
        description: data?.description || "",
        isCustom: activeTab,
        courseLink: activeTab ? data?.courseLink : "",
      },
      {
        onSuccess: async (data) => {
          toast.success("successfully update");
          await query.resetQueries({
            queryKey: ["getCourseData"],
            exact: true,
          });
          if (!activeTab) {
            navigate(`/courses/class/edit/${CourseData?.id}`);
          } else {
            navigate(`/courses`);
          }
        },
        onError: (error: any) => {
          toast.error(error.response.data.message);
        },
      }
    );
  };
  useEffect(() => {
    if (CourseData) {
      setValue("title", CourseData?.title);
      setValue("description", CourseData?.description);
      setValue("courseLink", CourseData?.courseLink);
      setActiveTab(CourseData?.isCustom);
    }
  }, [id, CourseData]);

  useEffect(() => {
    CourseRefetch();
  }, [id]);
  const breadcrumbItems = [
    { title: "Courses", link: "courses" },
    { title: "Edit" },
  ];
  return (
    <Sidebar>
      {CourseLoading ? (
        <Loader />
      ) : (
        <div className="mt-3">
          <CustomBreadcrumb items={breadcrumbItems} />
          <div className="flex justify-center">
            <div className="flex justify-center rounded-2xl bg-gray-200 p-2 ">
              {!activeTab ? (
                <button
                  className={`px-4 py-2  relative min-w-28 transition-all duration-300 font-medium rounded-xl ${
                    !activeTab ? "text-darkCyan bg-white " : "text-gray-400 "
                  }`}
                >
                  Custom
                </button>
              ) : (
                <button
                  className={`px-4 py-2 relative min-w-28 transition-all duration-300 font-medium rounded-xl ${
                    activeTab ? "text-darkCyan bg-white" : "text-gray-400 "
                  } `}
                >
                  Link
                </button>
              )}
            </div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-3">
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-3 mt-4">
              <InputField
                label="Title"
                placeholder="Enter title"
                type="text"
                register={register("title", { required: "title are required" })}
                error={errors.title?.message}
              />
              {activeTab && (
                <InputField
                  label="Link"
                  placeholder="Enter Link"
                  type="url"
                  register={register("courseLink", {
                    required: !activeTab ? false : "link are required",
                    pattern: {
                      value:
                        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                      message: "Please enter a valid URL",
                    },
                  })}
                  error={errors.courseLink?.message}
                />
              )}
            </div>
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-3 mt-4">
              <div className=" mt-2">
                <p className="block text-gray-700 mb-2">Description</p>
                <Controller
                  name="description"
                  control={control}
                  // rules={{ required: "description is required" }}
                  render={({ field }) => (
                    <ReactQuill
                      {...field}
                      // onChange={(value) => {
                      //   field.onChange(value);
                      // }}
                      className="quill-editor"
                      modules={{
                        toolbar: [
                          ["bold", "italic", "underline", "strike"],
                          ["blockquote", "code-block"],
                          [{ header: 1 }, { header: 2 }],
                          [{ list: "ordered" }, { list: "bullet" }],

                          [{ size: ["small", false, "large", "huge"] }],
                          [{ header: [1, 2, 3, 4, 5, 6, false] }],
                          [{ color: [] }, { background: [] }],

                          [{ align: [] }],
                          ["clean"],
                        ],
                      }}
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.description.message}
                  </p>
                )}
              </div>
              <div className="mt-2">
                <p className="block text-gray-700 mb-2">Upload Image</p>

                <div className="mt-3">
                  <label htmlFor="file">
                    <div
                      role="button"
                      className="flex flex-col justify-center items-center min-h-52 border-[1.5px] border-dashed border-gray-400 rounded-2xl"
                    >
                      {uploadedData ? (
                        // Display uploaded URL if present
                        <p className="text-center text-sm text-gray-600 text-ellipsis whitespace-nowrap overflow-hidden max-w-full">
                          {uploadedData.name}
                        </p>
                      ) : (
                        // Display upload prompt if nothing is uploaded
                        <>
                          {CourseData?.image ? (
                            <p className="text-center p-3 text-sm text-gray-600 text-ellipsis whitespace-nowrap overflow-hidden max-w-full">
                              {CourseData?.image}
                            </p>
                          ) : (
                            <>
                              <FileUploadIcon />
                              <CustomButton
                                btntext="Upload"
                                addclass="mt-3"
                                handleClick={() =>
                                  document.getElementById("file")?.click()
                                }
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </label>
                  <input
                    type="file"
                    className="hidden"
                    id="file"
                    accept={"image/jpeg, image/png, image/jpg"}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        setUploadedData(e.target.files[0]);
                      }
                    }}
                  />
                </div>
                {errors.image && (
                  <p className="text-red-500 text-sm mt-1">
                    {(errors.image as FieldError)?.message}
                  </p>
                )}
              </div>
            </div>
            <CustomButton
              type="submit"
              btntext="Submit"
              isLoading={coursePending || isUploading}
              addclass="mt-9 mx-auto w-1/2"
            />
            {/* <FileUploadModal modalOpen={modalOpen} setModalOpen={setModalOpen} /> */}
          </form>
        </div>
      )}
    </Sidebar>
  );
};

export default EditCourses;
